import React, { Component } from 'react';
import { Map, List, Set } from 'immutable';
import { getOfferResponse, getOfferResponseByOffer, getAirportsByIataCodes, getAccommodationSummary } from '../api';
import { createOfferIndex, getCombinedOffers, getReferencedAirportIataCodes, getAccommodationIDs } from './offerResponseUtils';
import offerResponseToRequest from '../request/offerResponseToRequest';
import scrollToComponent from 'react-scroll-to-component';

import MakeBooking from '../booking/MakeBooking';
import TripRequestEditor from '../request/TripRequestEditor';
import FlightOffersResponse from './FlightOffersResponse';
import AccommodationOffersResponse from './AccommodationOffersResponse';
import VehicleOffersResponse from './VehicleOffersResponse';
import BookingConfirmationResponse from '../booking/BookingConfirmationResponse';
import setThemeForCompany from '../theme/setThemeForCompany';
import classnames from 'classnames';
import moment from 'moment';

/* High level component for trip planning (flight, hotel and car).
This component keeps the state of these three views and render the
appropriate one based on user's selection of view

This can also be used to load a getOffersResponse by URI
*/

const tripPlannerViews = List.of('book-a-flight', 'get-a-room', 'rent-a-car');
const tripPlannersViewsNamesMap = new Map().set('book-a-flight', 'Flights').set('get-a-room', 'Accommodation').set('rent-a-car', 'Cars');

class TripPlanner extends Component {
  constructor() {
    super();
    this.state = {
      currentView: tripPlannerViews.get(0),
      // NDC response, loading/error flags, etc
      getOffersResponse: undefined,
      flightGetOffersResponse: undefined,
      flightCombinedOffersResponse: undefined,
      combinedOffersIndex: undefined,
      accommodationGetOffersResponse: undefined,

      getOffersResponseLoading: false,
      getOffersResponseError: undefined,
      loadingProgress: 25, // In percent

      // Reference data
      referenceData: { airportByIata: undefined },

      // Are we busy booking? (note, this is a hack, should use
      // different URL route + redux for shared state)
      busyBooking: false,
      settlingInvoice: "",
      processingPayment: false,
      paymentError: undefined,

      // User-selected offers
      // Map<GroupNo, Set<OfferUri>> - which offer is selected per-group
      // (single-selection mode, all we have for now)
      flightSelectionByOfferGroup: Map(),
      accommodationSelectionByOfferGroup: Map(),

      // Ui state and filtering
      // Note: Closed by default on small devices
      filterPanelOpen: typeof document !== 'undefined' && document.documentElement.clientWidth > 987,

      bookingConfirmations: List(),
      bookingConfirmationOpen: false,
      showCompact: false,
    };
  }

  toggleFilterPanel = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    this.setState({
      filterPanelOpen: !this.state.filterPanelOpen,
    });
  };

  componentDidMount() {
    if (this.props.match.params.offersResponseURI) {
      this.fetchOffersResponse(this.props.match.params.offersResponseURI);
    } else if (this.props.match.params.offerUri) {
      this.fetchOffersResponseForOffer(this.props.match.params.offerUri);
    } else if (this.props.match.params.compactOfferUri && this.props.match.params.compactOfferKey) {
      this.fetchOffersResponse(this.props.match.params.compactOfferUri, this.props.match.params.compactOfferKey);
    } else {
      setThemeForCompany(this.props.newTrip); // Start loading theme asap
    }
  }

  componentDidUpdate(previousProps) {
    if (previousProps.match.params.offersResponseURI) {
      if (
        previousProps.match.params.offersResponseURI !== this.props.match.params.offersResponseURI &&
        this.props.match.params.offersResponseURI !== ''
      ) {
        this.fetchOffersResponse(this.props.match.params.offersResponseURI);
      }
    } else if (previousProps.match.params.offerUri) {
      if (previousProps.match.params.offerUri !== this.props.match.params.offerUri && this.props.match.params.offerUri !== '') {
        this.fetchOffersResponseForOffer(this.props.match.params.offerUri);
      }
    } else if (previousProps.match.params.compactOfferUri && previousProps.match.params.compactOfferKey) {
      if (previousProps.match.params.compactOfferUri !== this.props.match.params.compactOfferUri && this.props.match.params.compactOfferUri !== '') {
        this.fetchOffersResponse(this.props.match.params.compactOfferUri, this.props.match.params.compactOfferKey);
      }
    }
  }

  /** Retrieves offerResponse by URI */
  fetchOffersResponse = (offersResponseURI, compactOfferKey = '') => {
    const offersResponseURIDec = decodeURIComponent(offersResponseURI);
    const compactOfferKeyDec = decodeURIComponent(compactOfferKey);

    // Note: We can decide on the desired behaviour here. Current
    // behaviour says we're "loading" until we have both the
    // offer response, and all airports, but there are other
    // options. In the current one, we also update 'progress'
    // for visual progress bar.
    this.setState({ getOffersResponseLoading: true });

    getOfferResponse(offersResponseURIDec)
      .then((res = Map()) => {
        console.log(res);
        const getOffersResponse = res.get('getOffersResponse');

        setThemeForCompany(getOffersResponse.get('company')); // Start loading theme asap

        this.setState({
          loadingProgress: 75,
          getOffersResponse,
        });
        return Promise.resolve(getOffersResponse);
      })
      .then((response) => {
        if (response.getIn(['offerGroup', 0, 'requested', 0, 'isA']) === 'FlightRequirement') {
          const flightCombinedOffersResponse = getCombinedOffers(response);
          const combinedOffersIndex = createOfferIndex(response);
          if (compactOfferKeyDec !== '') {
            this.selectOfferInGroup('flight', 0, combinedOffersIndex.get(0).get(compactOfferKeyDec));
          }
          this.setState({
            flightGetOffersResponse: response,
            flightCombinedOffersResponse: flightCombinedOffersResponse,
            showCompact: compactOfferKeyDec !== '',
            combinedOffersIndex: combinedOffersIndex,
          });

          getAirportsByIataCodes(getReferencedAirportIataCodes(response)).then((airportsList) => {
            this.setState({
              referenceData: {
                airportByIata: airportsList.reduce((m, airport) => m.set(airport.get('iataCode'), airport), this.state.referenceData.airportByIata),
              },
              getOffersResponseLoading: false,
              getOffersResponseError: null,
              loadingProgress: 100,
            });
          });
        } else {
          //if (response.getIn(['offerGroup', 0, 'requested',0, 'isA']) === 'AccommodationRequirement')
          this.setState({
            accommodationGetOffersResponse: response,
            loadingProgress: 85,
          });
          getAccommodationSummary(getAccommodationIDs(response)).then((accommodationSummaryList) => {
            this.setState({
              referenceData: {
                accommodationSummaryById: accommodationSummaryList.reduce((m, acc) => m.set(acc.getIn(['serviceProvider', 'uri']), acc), Map()),
              },
              getOffersResponseLoading: false,
              getOffersResponseError: null,
              loadingProgress: 100,
            });
          });
        }
      })
      .catch((getOffersResponseError) => {
        console.warn(getOffersResponseError);
        this.setState({
          getOffersResponseLoading: false,
          getOffersResponseError,
        });
      });
  };

  /** Retrieves offerResponse by offer URI */
  fetchOffersResponseForOffer = (offerUri) => {
    const offerUriDec = decodeURIComponent(offerUri);

    // Note: We can decide on the desired behaviour here. Current
    // behaviour says we're "loading" until we have both the
    // offer response, and all airports, but there are other
    // options. In the current one, we also update 'progress'
    // for visual progress bar.
    this.setState({ getOffersResponseLoading: true });

    getOfferResponseByOffer(offerUriDec)
      .then((res = Map()) => {
        const getOffersResponse = res.get('getOffersResponse');

        setThemeForCompany(getOffersResponse.get('company')); // Start loading theme asap

        this.setState({
          loadingProgress: 75,
          getOffersResponse,
        });
        return Promise.resolve(getOffersResponse);
      })
      .then((response) => {
        if (response.getIn(['offerGroup', 0, 'requested', 0, 'isA']) === 'FlightRequirement') {
          const index = response
            .get('offerGroup', List())
            .reduce((ind, og, ti) => (og.get('offer').reduce((c, o) => c || o.get('uri') === offerUriDec, false) ? ti : ind), 0);
          this.selectOfferInGroup('flight', index, offerUriDec);
          this.setState({
            flightGetOffersResponse: response,
            flightCombinedOffersResponse: getCombinedOffers(response),
            combinedOffersIndex: createOfferIndex(response),
          });
          getAirportsByIataCodes(getReferencedAirportIataCodes(response)).then((airportsList) => {
            this.setState({
              referenceData: {
                airportByIata: airportsList.reduce((m, airport) => m.set(airport.get('iataCode'), airport), this.state.referenceData.airportByIata),
              },
              getOffersResponseLoading: false,
              getOffersResponseError: null,
              loadingProgress: 100,
            });
          });
        } else {
          //if (response.getIn(['offerGroup', 0, 'requested',0, 'isA']) === 'AccommodationRequirement')
          const index = response
            .get('offerGroup', List())
            .reduce((ind, og, ti) => (og.get('offer').reduce((c, o) => o.get('uri') === offerUriDec, false) ? ti : ind), 0);
          this.selectOfferInGroup('accommodation', index, offerUriDec);
          this.setState({
            accommodationGetOffersResponse: response,
            loadingProgress: 85,
          });
          getAccommodationSummary(getAccommodationIDs(response)).then((accommodationSummaryList) => {
            this.setState({
              referenceData: {
                accommodationSummaryById: accommodationSummaryList.reduce((m, acc) => m.set(acc.getIn(['serviceProvider', 'uri']), acc), Map()),
              },
              getOffersResponseLoading: false,
              getOffersResponseError: null,
              loadingProgress: 100,
            });
          });
        }
      })
      .catch((getOffersResponseError) => {
        console.warn(getOffersResponseError);
        this.setState({
          getOffersResponseLoading: false,
          getOffersResponseError,
        });
      });
  };

  updateView = (currentView) => {
    if (!this.state.getOffersResponseLoading) {
      this.setState({ currentView });
    }
  };

  updateReferenceData = (referenceData) => {
    this.setState({ referenceData });
  };

  updateOffersResponse = (view, response) => {
    // console.log(response.toString());
    if (!response.get('success', true)) {
      console.log('unexpected error');
    } else if (view === tripPlannerViews.get(0)) {
      getAirportsByIataCodes(getReferencedAirportIataCodes(response)).then((airportsList) => {
          this.setState({
            referenceData: {
              airportByIata: airportsList.reduce((m, airport) => m.set(airport.get('iataCode'), airport), this.state.referenceData.airportByIata),
            },
          });
      });
      this.setState({
        flightSelectionByOfferGroup: new Map(),
        getOffersResponse: response,
        flightGetOffersResponse: response,
        flightCombinedOffersResponse: getCombinedOffers(response),
        combinedOffersIndex: createOfferIndex(response),
      });
    } else if (view === tripPlannerViews.get(1)) {
      getAccommodationSummary(getAccommodationIDs(response)).then((accommodationSummaryList) => {
        this.setState({
          referenceData: {
            accommodationSummaryById: accommodationSummaryList.reduce((m, acc) => m.set(acc.getIn(['serviceProvider', 'uri']), acc), Map()),
          },
        });
      });
      this.setState({
        accommodationSelectionByOfferGroup: new Map(),
        accommodationGetOffersResponse: response,
        getOffersResponse: response,
      });
    } else if (view === tripPlannerViews.get(2)) {
      this.setState({
        vehicleGetOffersResponse: response,
        getOffersResponse: response,
      });
    }
  };

  offersRequestStatus = (status) => {
    this.setState({ ...status });
  };

  /** Temporary until Redux + react-router refactoring. */
  startBooking = () => {
    this.setState({
      busyBooking: true,
    });
  };

  stopBooking = () => {
    this.setState({
      busyBooking: false,
    });
  };

  setSettlingInvoice = (flag) => {
    this.setState({
      settlingInvoice: flag,
      processingPayment: false,
      paymentError: undefined,
    });
  };

  setProcessingPayment = (flag) => {
    this.setState({
      processingPayment: flag,
    });
  };

  setPaymentError = (message) => {
    this.setState({
      paymentError: message,
      processingPayment: false,
      settlingInvoice: "",
    });
  };

  updateBookingConfirmation = (bookingConfirmations = List()) => {
    this.setState(
      {
        bookingConfirmations: bookingConfirmations,
        bookingConfirmationOpen: true,
        busyBooking: false,
        settlingInvoice: "",
        processingPayment: false,
      },
      () => {
        scrollToComponent(this.bookingConfirmationElement, { offset: 0, align: 'top', duration: 500, ease: 'inQuad' });
      }
    );
  };

  closeBookingConfirmation = () => {
    this.setState({ bookingConfirmationOpen: false });
  };

  selectOfferInGroup = (serviceType) => (index, offerUri) => {
    let newState = {};
    // Enforce single-selection for now
    newState[serviceType + 'SelectionByOfferGroup'] = this.state[serviceType + 'SelectionByOfferGroup'].set(index, Set.of(offerUri));
    this.setState(newState);
  };

  unselectOfferInGroup = (serviceType) => (index, offerUri) => {
    // Enforce single-selection for now
    let newState = {};
    newState[serviceType + 'SelectionByOfferGroup'] = this.state[serviceType + 'SelectionByOfferGroup'].set(index, Set());
    this.setState(newState);
  };

  render() {
    const {
      currentView,
      busyBooking,
      settlingInvoice,
      processingPayment,
      paymentError,
      bookingConfirmationOpen,
      bookingConfirmations,
      flightGetOffersResponse = Map(),
      flightCombinedOffersResponse,
      combinedOffersIndex,
      accommodationGetOffersResponse = Map(),
      referenceData,
      getOffersResponseError,
      getOffersResponseLoading,
      loadingProgress,
      flightSelectionByOfferGroup,
      accommodationSelectionByOfferGroup,
    } = this.state;

    let isNewTrip = this.props.newTrip !== undefined && flightGetOffersResponse.size === 0 && accommodationGetOffersResponse.size === 0;

    let tripRequest = isNewTrip
      ? new Map()
      : currentView === tripPlannerViews.get(0)
      ? offerResponseToRequest(flightGetOffersResponse)
      : currentView === tripPlannerViews.get(1)
      ? offerResponseToRequest(this.state.accommodationGetOffersResponse).setIn(
          ['required', 0, 'approximateLocation', 'address', 'namedAddress'],
          'Dummy Location'
        )
      : offerResponseToRequest(this.state.vehicleGetOffersResponse);

    if (tripRequest.get('company') === undefined) {
      tripRequest = tripRequest
        .set('company', currentView === tripPlannerViews.get(0) ? this.props.newTrip : 'http://www.myclient.com')
        .set('participants', Map.of('participant', List.of(Map.of('ageClassification', 'ADULT'))))
        .set('required', List.of(Map.of('isA', 'FilghtRequirement')));

      if (currentView === tripPlannerViews.get(0)) {
        tripRequest = tripRequest
          .setIn(
            ['required', 0, 'departureDate'],
            moment()
              // moment("2020-04-08")
              .add(1, 'days')
              .format('YYYY-MM-DD')
          )
          // .setIn(["required", 0, "from"], "JNB")
          // .setIn(["required", 0, "to"], "LON")
          .setIn(['required', 0, 'cabinClass'], 'ECONOMY');
      }

      if (currentView === tripPlannerViews.get(1)) {
        tripRequest = tripRequest
          .setIn(['required', 0, 'checkIn'], moment().add(1, 'days').format('YYYY-MM-DD'))
          .setIn(['required', 0, 'checkOut'], moment().add(2, 'days').format('YYYY-MM-DD'));
      }
    }

    const offerGroups =
      currentView === isNewTrip
        ? new List()
        : currentView === tripPlannerViews.get(0)
        ? flightGetOffersResponse.get('offerGroup', List())
        : currentView === tripPlannerViews.get(1)
        ? accommodationGetOffersResponse.get('offerGroup', List())
        : new List();

    return busyBooking && currentView === tripPlannerViews.get(0) ? (
      <MakeBooking
        requiredPassenger={tripRequest.getIn(['participants', 'participant'], List())}
        updateBookingConfirmation={this.updateBookingConfirmation}
        company={flightGetOffersResponse.get('company')}
        selectedOffers={selectedOfferList(flightGetOffersResponse.get('offerGroup', List()), flightSelectionByOfferGroup)}
        airportByIata={referenceData.airportByIata}
        cancel={this.stopBooking}
      />
    ) : bookingConfirmationOpen ? (
      <BookingConfirmationResponse
        updateBookingConfirmation={this.updateBookingConfirmation}
        company={flightGetOffersResponse.get('company')}
        setSettlingInvoice={this.setSettlingInvoice}
        settlingInvoice={settlingInvoice}
        setProcessingPayment={this.setProcessingPayment}
        processingPayment={processingPayment}
        setPaymentError={this.setPaymentError}
        paymentError={paymentError}
        close={this.closeBookingConfirmation}
        bookingConfirmations={bookingConfirmations}
        airportByIata={referenceData.airportByIata}
        forwardRef={(elem) => {
          this.bookingConfirmationElement = elem;
        }}
      />
    ) : (
      <div>
        {/*ELSE !bookingConfirmationOpen block*/}
        <div className='container'>
          <nav className='plan-a-trip-nav'>
            <ul className='row nav'>
              {tripPlannerViews.map((view, i) => (
                <li className='col-xs-8' key={i}>
                  <a
                    className={classnames(view, {
                      active: currentView === view,
                    })}
                    style={{ cursor: 'pointer' }}
                    onClick={this.updateView.bind(this, view)}
                  >
                    {tripPlannersViewsNamesMap.get(view)}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </div>
        <TripRequestEditor
          tripRequest={tripRequest}
          view={currentView}
          onUpdateOffersResponse={this.updateOffersResponse}
          referenceData={referenceData}
          onUpdateReferenceData={this.updateReferenceData}
          offersRequestStatus={this.offersRequestStatus}
        />
        {!getOffersResponseLoading && getOffersResponseError == null && offerGroups.size > 0 ? (
          currentView === tripPlannerViews.get(0) ? (
            <FlightOffersResponse
              flightGetOffersResponse={flightGetOffersResponse}
              flightCombinedOffersResponse={flightCombinedOffersResponse}
              combinedOffersIndex={combinedOffersIndex}
              selectOffer={this.selectOfferInGroup('flight')}
              unselectOffer={this.unselectOfferInGroup('flight')}
              selectionByOfferGroup={flightSelectionByOfferGroup}
              referenceData={referenceData}
              startBooking={this.startBooking}
              toggleFilterPanel={this.toggleFilterPanel}
              filterPanelOpen={this.state.filterPanelOpen}
              showCompact={this.state.showCompact}
            />
          ) : currentView === tripPlannerViews.get(1) ? (
            <AccommodationOffersResponse
              accommodationGetOffersResponse={accommodationGetOffersResponse}
              selectOffer={this.selectOfferInGroup('accommodation')}
              unselectOffer={this.unselectOfferInGroup('accommodation')}
              selectionByOfferGroup={accommodationSelectionByOfferGroup}
              referenceData={referenceData}
              startBooking={this.startBooking}
              toggleFilterPanel={this.toggleFilterPanel}
              filterPanelOpen={this.state.filterPanelOpen}
            />
          ) : (
            <VehicleOffersResponse />
          )
        ) : null}
        {getOffersResponseError && (
          <div className='container'>
            <div className='alert alert-danger'>Failed to retrieve offer, sorry!</div>
          </div>
        )}
        {getOffersResponseLoading && (
          <div className='container'>
            <div className='progress'>
              <div className='progress-bar progress-bar-striped active' role='progressbar' style={{ width: `${loadingProgress}%` }}>
                <span className='sr-only'>Processing...</span>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

/** Util to build a List<Offer> given the offergroups and user selection.
 * @param offerGroups List<OfferGroup>
 * @param selectionByGroup Map<Int, Set<OfferUri>>
 */
const selectedOfferList = (offerGroups = List(), selectionByGroup = Map()) =>
  // Simply assumes 1 selected per group - otherwise needs different algorithm
  offerGroups.map((og, n) => og.get('offer', List()).find((o) => o.get('uri') === selectionByGroup.get(n, Set()).first()));

export default TripPlanner;



// WEBPACK FOOTER //
// ./src/ndc/offer/TripPlanner.js